import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit } from '@mui/icons-material';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import { CaseHistoryResponseDto } from '@bandido/shared/dtos';
import { CaseLogActions } from '@bandido/shared/constants';

import { Timeline } from '../Timeline';
import { MessageContainer } from '../EmailItem/styled';
import { LogEntry } from './LogEntry';
import { LogContainer, LogHeader, LogContent } from './styled';

interface CaseLogProps {
  authorFullName: string;
  isCurrentUser: (history: CaseHistoryResponseDto) => boolean;
  messages: CaseHistoryResponseDto[];
}

const CaseLog = forwardRef<HTMLDivElement, CaseLogProps>(({ isCurrentUser, authorFullName, messages }, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const currentUser = isCurrentUser(messages[0]);
  const isCreatedCaseLog = messages[0].caseLogAction === CaseLogActions.CREATE;
  const action = isCreatedCaseLog ? 'caseCreate' : 'caseEdit';

  return (
    <MessageContainer ref={ref} sx={{ justifyContent: currentUser ? 'flex-end' : 'flex-start' }}>
      <Stack
        overflow="hidden"
        order={currentUser ? 1 : 2}
        mr={(theme) => theme.spacing(currentUser ? 2.5 : 0)}
        ml={(theme) => theme.spacing(currentUser ? 0 : 2.5)}
      >
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent={currentUser ? 'flex-end' : 'flex-start'}
          alignItems="center"
          pb={1}
          gap={2}
        >
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="subtitle2">{authorFullName}</Typography>
            <Typography textTransform="lowercase" variant="body2" color="text.secondary">
              {t(`cases.view.content.${action}`)}
            </Typography>
          </Box>
        </Stack>

        {!isCreatedCaseLog && (
          <LogContainer>
            <LogHeader>
              <Edit sx={{ color: 'text.secondary' }} />
              <Typography color="text.secondary" variant="subtitle2">
                {t('cases.view.content.edit')} ({messages.length})
              </Typography>
            </LogHeader>
            <LogContent>
              {messages.map((log) => (
                <LogEntry key={log.id} log={log} />
              ))}
            </LogContent>
          </LogContainer>
        )}
      </Stack>

      <Timeline isCurrentUser={currentUser} icon={Edit} color="text.secondary" background={theme.palette.grey[300]} />
    </MessageContainer>
  );
});

CaseLog.displayName = 'CaseLogItem';

export default CaseLog;
